import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { EXCHANGE } from "../../Services/ExchangeSelectorContextProvider";
import {
  SelectedContract,
  usePrefillContext,
} from "../../Services/PrefillContextProvider";
import { useMessages } from "../../Services/Nats";
import { useListedAlgo } from "../lazertrader/contexts/listAlgosContext";
import { OrderBookSnapshot } from "./types";

export const useOrderBookSnapshot = (
  contractId: number | undefined,
  deliveryArea: string | undefined
) => {
  const enabled = contractId !== undefined && deliveryArea !== undefined;

  const { data, isLoading, error } = useQuery<OrderBookSnapshot>({
    queryKey: [`v1/${EXCHANGE}/orderbook/${contractId}/${deliveryArea}`],
    refetchInterval: 2000,
    staleTime: 0,
    enabled,
  });

  return {
    snapshot: data,
    loading: isLoading,
    error,
  };
};

type DepthContract = Pick<SelectedContract, "contractId" | "areaCode">;

/**
 * The contract behind a single selected algo, so clicking an algo shows its
 * depth the way clicking an order book cell does.
 *
 * An algo carries no contract id — only its area and delivery window — so the
 * id is looked up in the live CONTRACTS feed. The window is matched as the raw
 * ISO strings, the same comparison the order book's row highlight makes
 * (`rowId` in `orderBook/hooks.tsx`), so whenever an algo highlights its row it
 * also finds its contract here. `undefined` for none or several algos, or once
 * the contract has expired out of the feed.
 */
const useSelectedAlgoContract = (): DepthContract | undefined => {
  const { selectedAlgos } = useListedAlgo();
  const { contracts } = useMessages();

  // LT sends all pdefs but this app only ever works with the first.
  const pdef =
    selectedAlgos.length === 1
      ? selectedAlgos[0].product_definitions[0]
      : undefined;

  return useMemo(() => {
    if (pdef === undefined) {
      return undefined;
    }

    const areaCode = pdef.delivery_area_id;
    const contract = Object.values(contracts[areaCode] ?? {}).find(
      (c) =>
        c.delivery_start === pdef.delivery_start &&
        c.delivery_end === pdef.delivery_end
    );

    return contract === undefined
      ? undefined
      : { contractId: contract.contract_identifier.contract_id, areaCode };
  }, [pdef, contracts]);
};

/**
 * Depth for whatever the desk is looking at: the order book cell it clicked,
 * or failing that the one algo it selected. Selecting an algo resets the
 * prefill, so the two never compete — the click simply wins while it lasts.
 */
export const useSelectedOrderBookSnapshot = () => {
  const { selectedContract: clicked } = usePrefillContext();
  const fromAlgo = useSelectedAlgoContract();
  const selectedContract: DepthContract | undefined = clicked ?? fromAlgo;

  const result = useOrderBookSnapshot(
    selectedContract?.contractId,
    selectedContract?.areaCode
  );

  return { ...result, selectedContract };
};
